import { useEffect, useMemo, useState } from "react";
import { Edit, Plus, Trash2, X } from "lucide-react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import { Table } from "../../../../components/table";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { AiModel, AiModelPayload } from "../../../../types/ai-model";

type AiModelFormState = {
  id?: string;
  vendor: string;
  model: string;
  input: string;
  cached: string;
  output: string;
};

const emptyForm: AiModelFormState = {
  vendor: "",
  model: "",
  input: "",
  cached: "",
  output: "",
};

function formatPrice(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value);
}

function createFormFromModel(model: AiModel): AiModelFormState {
  return {
    id: model.id,
    vendor: model.vendor,
    model: model.model,
    input: String(model.input),
    cached: String(model.cached),
    output: String(model.output),
  };
}

function parsePrice(value: string): number {
  const normalized = value.trim().replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

export default function AiModelsPage() {
  const { success, error, warn } = useToast();

  const [models, setModels] = useState<AiModel[]>([]);
  const [form, setForm] = useState<AiModelFormState>(emptyForm);
  const [modelToDelete, setModelToDelete] = useState<AiModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isEditing = Boolean(form.id);
  const vendorsCount = useMemo(
    () => new Set(models.map((item) => item.vendor)).size,
    [models],
  );

  useEffect(() => {
    void fetchModels();
  }, []);

  async function fetchModels() {
    try {
      setIsLoading(true);
      const response = await api.get<AiModel[]>("/ai-models");
      setModels(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      error(getApiErrorMessage(err));
      setModels([]);
    } finally {
      setIsLoading(false);
    }
  }

  function buildPayload(): AiModelPayload | null {
    const payload = {
      vendor: form.vendor.trim(),
      model: form.model.trim(),
      input: parsePrice(form.input),
      cached: parsePrice(form.cached),
      output: parsePrice(form.output),
    };

    if (!payload.vendor) {
      warn("Informe o fornecedor.");
      return null;
    }

    if (!payload.model) {
      warn("Informe o modelo.");
      return null;
    }

    if (
      !Number.isFinite(payload.input) ||
      !Number.isFinite(payload.cached) ||
      !Number.isFinite(payload.output)
    ) {
      warn("Informe valores numéricos para input, cached e output.");
      return null;
    }

    if (payload.input < 0 || payload.cached < 0 || payload.output < 0) {
      warn("Os valores de custo não podem ser negativos.");
      return null;
    }

    return payload;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (isSubmitting) return;

    const payload = buildPayload();
    if (!payload) return;

    try {
      setIsSubmitting(true);

      if (form.id) {
        await api.patch(`/ai-models/${form.id}`, payload);
        success("Modelo atualizado com sucesso.");
      } else {
        await api.post("/ai-models", payload);
        success("Modelo cadastrado com sucesso.");
      }

      setForm(emptyForm);
      await fetchModels();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function deleteModel(id: string) {
    if (isDeleting) return;

    try {
      setIsDeleting(true);
      await api.delete(`/ai-models/${id}`);
      setModelToDelete(null);
      if (form.id === id) {
        setForm(emptyForm);
      }
      success("Modelo removido com sucesso.");
      await fetchModels();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  }

  const columns = [
    {
      key: "model",
      label: "Modelo",
      render: (item: AiModel) => (
        <div>
          <p className="font-medium text-slate-900">{item.model}</p>
          <p className="mt-1 text-xs text-slate-500">{item.vendor}</p>
        </div>
      ),
    },
    {
      key: "input",
      label: "Input",
      render: (item: AiModel) => formatPrice(item.input),
    },
    {
      key: "cached",
      label: "Cached",
      render: (item: AiModel) => formatPrice(item.cached),
    },
    {
      key: "output",
      label: "Output",
      render: (item: AiModel) => formatPrice(item.output),
    },
    {
      key: "actions",
      label: "Ações",
      render: (item: AiModel) => (
        <div className="flex justify-center gap-3 text-center">
          <button
            type="button"
            onClick={() => setForm(createFormFromModel(item))}
            className="btn-link-primary cursor-pointer"
            disabled={!item.id || isSubmitting}
            title="Editar modelo"
          >
            <Edit className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => setModelToDelete(item)}
            className="btn-link-danger cursor-pointer"
            disabled={!item.id}
            title="Excluir modelo"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="app-page">
      <BackButton />

      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="app-page-title">Modelos de IA</h1>
          <p className="app-page-subtitle">
            {models.length} modelos em {vendorsCount} fornecedor(es)
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="app-card mb-6 p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="app-section-title">
            {isEditing ? "Editar modelo" : "Novo modelo"}
          </h2>
          {isEditing && (
            <button
              type="button"
              onClick={() => setForm(emptyForm)}
              className="btn btn-secondary cursor-pointer"
              disabled={isSubmitting}
            >
              <X className="h-4 w-4" />
              Cancelar edição
            </button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Fornecedor
            <input
              type="text"
              value={form.vendor}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  vendor: event.target.value,
                }))
              }
              className="form-input"
              disabled={isSubmitting}
              required
            />
          </label>

          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Modelo
            <input
              type="text"
              value={form.model}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  model: event.target.value,
                }))
              }
              className="form-input"
              disabled={isSubmitting}
              required
            />
          </label>

          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Input
            <input
              type="number"
              value={form.input}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  input: event.target.value,
                }))
              }
              min="0"
              step="0.001"
              className="form-input"
              disabled={isSubmitting}
              required
            />
          </label>

          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Cached
            <input
              type="number"
              value={form.cached}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  cached: event.target.value,
                }))
              }
              min="0"
              step="0.001"
              className="form-input"
              disabled={isSubmitting}
              required
            />
          </label>

          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Output
            <input
              type="number"
              value={form.output}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  output: event.target.value,
                }))
              }
              min="0"
              step="0.001"
              className="form-input"
              disabled={isSubmitting}
              required
            />
          </label>
        </div>

        <div className="mt-4 flex justify-end">
          <LoadingButton
            type="submit"
            className="btn btn-neutral cursor-pointer"
            isLoading={isSubmitting}
            loadingLabel={isEditing ? "Salvando" : "Cadastrando"}
          >
            <Plus className="h-4 w-4" />
            {isEditing ? "Salvar alterações" : "Cadastrar modelo"}
          </LoadingButton>
        </div>
      </form>

      <Table<AiModel>
        columns={columns}
        data={models}
        emptyMessage={
          isLoading ? "Carregando modelos..." : "Nenhum modelo cadastrado."
        }
      />

      <ConfirmationModal
        isOpen={!!modelToDelete}
        title="Confirmar exclusão"
        description={
          modelToDelete
            ? `Tem certeza que deseja excluir o modelo ${modelToDelete.model}?`
            : ""
        }
        onCancel={() => setModelToDelete(null)}
        onConfirm={() => modelToDelete?.id && deleteModel(modelToDelete.id)}
        isLoading={isDeleting}
        confirmLabel="Excluir"
      />
    </div>
  );
}
